/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useRef } from 'react';

import * as Sentry from '@sentry/react';
import type { HostedFields } from 'braintree-web';

import useFluxibleAction from 'js/lib/fluxibleHooks/useFluxibleAction';
import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';

import { VisuallyHidden } from '@coursera/cds-core';

import {
  clearBraintreeHostedFields,
  setBraintreeClientToken,
  setBraintreeHostedFields,
  setCreditCardData,
  updateCountry,
  updateZipcode,
} from 'bundles/account-settings/actions/EnterCardFormActions';
import BraintreeHostedFieldFormGroup from 'bundles/account-settings/components/sections/payment-method/BraintreeHostedFieldFormGroupController';
import CdsCreditCardSafetyNotice from 'bundles/account-settings/components/sections/payment-method/CdsCreditCardSafetyNotice';
import PaymentFormErrors from 'bundles/account-settings/components/sections/payment-method/PaymentFormErrors';
import EnterCardFormStore from 'bundles/account-settings/stores/EnterCardFormStore';
import LoadingIcon from 'bundles/courseraLoadingIcon/LoadingIcon';
import { useNaptime } from 'bundles/naptimejs';
/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import PaymentProcessorClientTokensV1 from 'bundles/naptimejs/resources/paymentProcessorClientTokens.v1';
import InputFormGroup from 'bundles/payments-common/components/inputs/InputFormGroup';
import LocationFormGroup from 'bundles/payments-common/components/inputs/LocationFormGroup';
import { conditionallyCreateBraintreeHostedFields } from 'bundles/payments-common/utils/braintreeClientUtils';

import _t from 'i18n!nls/account-settings';

type PropsFromCaller = {
  isSubmitted: boolean;
  validationErrorCode: string | null;
  validationErrors: Array<string | null>;
};

type PropsFromNaptime = {
  braintreeClientTokenResponse: PaymentProcessorClientTokensV1;
};

type Props = PropsFromCaller & PropsFromNaptime;

const styles = {
  form: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  safetyNotice: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  credentialsCol: css`
    display: flex;
    gap: var(--cds-spacing-200);
  `,
  hostedFieldContainer: css`
    label {
      text-transform: none;
      font: var(--cds-typography-subtitle-medium);

      svg {
        margin-left: var(--cds-spacing-50);
        vertical-align: middle;
      }
    }

    /* Braintree injects absolutely-positioned iframes; without a positioned
       ancestor, taps on mobile miss the iframe and don't focus the field. */
    .rc-BraintreeHostedFieldFormGroup {
      position: relative;
    }

    .bt-placeholder {
      border: 1px solid var(--cds-color-neutral-stroke-primary);
      border-radius: var(--cds-border-radius-100);
      padding: 0;
      background: var(--cds-color-neutral-background-primary);
      height: var(--cds-spacing-600);
      box-sizing: border-box;
    }

    .bt3-has-error .bt-placeholder {
      border-color: var(--cds-color-feedback-error);
    }

    .bt3-has-success .bt-placeholder {
      border-color: var(--cds-color-feedback-success);
    }
  `,
  cvvDescription: css`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  `,
};

// Braintree hosted fields render in a cross-origin iframe that can't resolve our CSS variables.
// Values here must be hardcoded to match CDS bodyPrimary / neutral-primary-weak.
const braintreeInputStyles = {
  input: {
    color: '#5b6780',
    'font-size': '16px',
    'font-family': '"Source Sans 3", "Source Sans Pro", Arial, sans-serif',
    'line-height': '24px',
    padding: '0 12px',
  },
  'input::placeholder': {
    color: '#5b6780',
    opacity: '1',
  },
  '.invalid, .blurry, .form-submitted': {
    color: '#D30001',
  },
  '.valid, .blurry.valid, .form-submitted.valid': {
    color: '#1F8354',
  },
};

const EnterCardFormInner = ({
  isSubmitted,
  validationErrorCode,
  validationErrors,
  braintreeClientTokenResponse,
}: Props) => {
  const executeAction = useFluxibleAction();
  const braintreeHostedFieldsRef = useRef<HostedFields | undefined>(undefined);

  const { creditCardData, billingCountry, billingZipcode, braintreeHostedFields } = useFluxibleStore(
    EnterCardFormStore,
    (store) => ({
      creditCardData: store.getCreditCardData(),
      billingCountry: store.getBillingInformation()?.billingCountry,
      billingZipcode: store.getBillingInformation()?.billingZipcode,
      braintreeHostedFields: store.getBraintreeHostedFields(),
    })
  );

  braintreeHostedFieldsRef.current = braintreeHostedFields;

  const getBraintreeClientToken = useCallback<() => string>(
    () => braintreeClientTokenResponse.definition.clientToken,
    [braintreeClientTokenResponse]
  );

  const createHostedFields = useCallback(() => {
    conditionallyCreateBraintreeHostedFields(
      getBraintreeClientToken,
      braintreeInputStyles,
      braintreeHostedFieldsRef.current
    ).then((hostedFields) => {
      if (hostedFields) {
        executeAction(setBraintreeHostedFields, { braintreeHostedFields: hostedFields });
      }
    });
  }, [getBraintreeClientToken, executeAction]);

  /* eslint-disable react-hooks/exhaustive-deps -- mount-only: wrapper guarantees stable props */
  useEffect(() => {
    const braintreeClientToken = braintreeClientTokenResponse.definition.clientToken;
    executeAction(setBraintreeClientToken, { braintreeClientToken });
    createHostedFields();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const prevTokenRef = useRef(braintreeClientTokenResponse.definition.clientToken);
  useEffect(() => {
    const currentToken = braintreeClientTokenResponse.definition.clientToken;
    if (prevTokenRef.current !== currentToken) {
      prevTokenRef.current = currentToken;
      createHostedFields();
    }
  }, [braintreeClientTokenResponse, createHostedFields]);

  useEffect(() => {
    return () => {
      if (braintreeHostedFieldsRef.current) {
        executeAction(clearBraintreeHostedFields);
        braintreeHostedFieldsRef.current.teardown();
      }
    };
  }, [executeAction]);

  const onUpdateName = useCallback(
    (cardholderName?: string | null) => {
      executeAction(setCreditCardData, { cardholderName: cardholderName ?? undefined });
    },
    [executeAction]
  );

  const onUpdateAddress = useCallback(
    (fieldName: string, value?: string | null) => {
      switch (fieldName) {
        case 'country':
          executeAction(updateCountry, { country: value ?? '' });
          break;
        case 'zipcode':
        case 'postalCode':
          executeAction(updateZipcode, { zipcode: value ?? '' });
          break;
        default:
          Sentry.captureMessage(`Unsupported address field in EnterCardForm: ${fieldName}`, {
            level: 'warning',
          });
      }
    },
    [executeAction]
  );

  const { cardholderName } = creditCardData;

  return (
    <div css={styles.hostedFieldContainer}>
      <div css={styles.safetyNotice}>
        <CdsCreditCardSafetyNotice />
      </div>
      {validationErrorCode && <PaymentFormErrors validationErrors={validationErrors} />}
      <form id="coursera-payment-braintree-form" css={styles.form}>
        <InputFormGroup
          labelText={_t('Name on card')}
          htmlAttributes={{ 'data-e2e': 'cc-name' }}
          placeholder={_t('Full name on card')}
          name="ccFullName"
          onChange={onUpdateName}
          validateFn={(name: $TSFixMe) => name.length > 1}
          defaultValue={cardholderName}
          isFormSubmitted={isSubmitted}
          isVerticallyAligned={true}
          inputId="cc-name"
          isRequired={true}
          showCdsDesign={true}
        />
        <BraintreeHostedFieldFormGroup
          braintreeFieldName="number"
          labelText={_t('Card Number')}
          isFormSubmitted={isSubmitted}
          isVerticallyAligned={true}
        />
        <div css={styles.credentialsCol}>
          <BraintreeHostedFieldFormGroup
            braintreeFieldName="expirationDate"
            labelText={_t('Expiration Date')}
            isFormSubmitted={isSubmitted}
            isVerticallyAligned={true}
          />
          <BraintreeHostedFieldFormGroup
            braintreeFieldName="cvv"
            labelText="CVV"
            isFormSubmitted={isSubmitted}
            isVerticallyAligned={true}
          />
        </div>

        <p css={styles.cvvDescription} id="cvv-description">
          {_t("Enter your credit card's three or four-digit security code.")}
        </p>

        <LocationFormGroup
          address={{
            country: billingCountry,
            zipcode: billingZipcode,
          }}
          isFormSubmitted={isSubmitted}
          isTabLayout={true}
          onValidate={null}
          shouldAlwaysTriggerChange={true}
          onAddressChange={onUpdateAddress}
          showCdsDesign={true}
        />
      </form>
    </div>
  );
};

const EnterCardForm = ({ isSubmitted, validationErrorCode, validationErrors }: PropsFromCaller) => {
  const {
    data: { braintreeClientTokenResponse },
    pending,
  } = useNaptime<{ braintreeClientTokenResponse: PaymentProcessorClientTokensV1 }>(
    () => ({
      braintreeClientTokenResponse: PaymentProcessorClientTokensV1.get('braintree'),
    }),
    []
  );

  if (pending || !braintreeClientTokenResponse) {
    return (
      <div>
        <VisuallyHidden role="status" aria-live="polite">
          {_t('Loading payment settings')}
        </VisuallyHidden>
        <LoadingIcon />
      </div>
    );
  }

  return (
    <EnterCardFormInner
      isSubmitted={isSubmitted}
      validationErrorCode={validationErrorCode}
      validationErrors={validationErrors}
      braintreeClientTokenResponse={braintreeClientTokenResponse}
    />
  );
};

export default EnterCardForm;
